import React, { useState } from 'react';
import * as consts from 'config/consts';
import {
  refreshMoneytree,
  syncAccountAsset,
  GetUserBrokerResultItem,
} from 'config/api';
import Modal2 from 'components/commons/modal/Modal2';
import Loading from 'components/commons/loading/Loading';
import { getUserStorage } from '../utils/utils';

type RefreshMoneytreeResponseProps =
  | {
      tag: 'Updated';
      status: boolean;
    }
  | { tag: 'NotUpdated' };

export type SyncAccountAssetResponseProps =
  | {
      tag: 'Updated';
      resultCode: number;
    }
  | { tag: 'NotUpdated' };

export const AccountUpdateStoppedModal = ({
  redirectPath,
  brokerItems,
  isShow,
  hide,
}: {
  redirectPath: string;
  brokerItems: GetUserBrokerResultItem[] | null;
  isShow: boolean;
  hide: () => void;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refreshMoneytreeResponse, setRefreshMoneytreeResponse] = useState<
    RefreshMoneytreeResponseProps
  >({
    tag: 'NotUpdated',
  });
  const [syncAccountAssetResponse, setSyncAccountAssetResponse] = useState<
    SyncAccountAssetResponseProps
  >({
    tag: 'NotUpdated',
  });

  const refreshAllAccountInfo = async (): Promise<void> => {
    const userStorage = getUserStorage();
    if (userStorage === null || brokerItems === null) return;
    setIsLoading(true);

    const retRefreshMoneytree = await refreshMoneytree();
    setRefreshMoneytreeResponse({
      tag: 'Updated',
      status: retRefreshMoneytree.Success,
    });

    if (userStorage.Charged) {
      const retSyncAccountAsset = await syncAccountAsset();
      setSyncAccountAssetResponse({
        tag: 'Updated',
        resultCode: retSyncAccountAsset.ResultCode,
      });

      setIsLoading(false);
    } else {
      const brokerItemsMONO = brokerItems.filter(
        item => item.AggregateSource === consts.AggregateSourceTypes.MONO
      );
      const isAllDataSbi = brokerItemsMONO.every(
        item => item.BrokerID === consts.MONOBrokerIds.SBI証券
      );

      if (isAllDataSbi) {
        const retSyncAccountAsset = await syncAccountAsset();
        setSyncAccountAssetResponse({
          tag: 'Updated',
          resultCode: retSyncAccountAsset.ResultCode,
        });

        setIsLoading(false);
      } else {
        const sbiList = brokerItemsMONO.filter(
          item => item.BrokerID === consts.MONOBrokerIds.SBI証券
        );
        if (sbiList.length === 0) return;

        (async () => {
          const resultCodeList = await Promise.all(
            sbiList.map(async item => {
              const retSyncAccountAsset = await syncAccountAsset(item.Account);
              return retSyncAccountAsset.ResultCode;
            })
          );
          const isSameResult = resultCodeList.every(
            code => code === resultCodeList[0]
          );

          await setSyncAccountAssetResponse({
            tag: 'Updated',
            // memo: isSameResultがfalseの場合はモーダルに表示する文言を「更新できませんでした」にするため、適当な数値を設定する。
            resultCode: isSameResult ? resultCodeList[0] : 999,
          });

          await setIsLoading(false);
        })();
      }
    }
  };

  const close = () => {
    hide();
    window.location.href = redirectPath;
  };

  if (isLoading) {
    return (
      <Modal2 isShow={isShow}>
        <div className="p-4 max-h-modal overflow-y-auto">
          <div className="flex flex-row">
            <div className="font-bold text-bluegray-700 text-base">
              しばらくお待ちください
            </div>
            <div className="icon-close ml-auto text-gray-500 cursor-pointer" />
          </div>
          <div className="my-20 flex justify-center items-center">
            <Loading
              loading={true}
              circularLoading={true}
              style={{
                backgroundColor: 'transparent',
              }}
            />
          </div>
        </div>
      </Modal2>
    );
  }

  if (
    refreshMoneytreeResponse.tag === 'Updated' &&
    syncAccountAssetResponse.tag === 'Updated'
  ) {
    const resultCode = syncAccountAssetResponse.resultCode;
    return (
      <Modal2 isShow={isShow}>
        <div className="p-4 max-h-modal overflow-y-auto">
          <div className="flex flex-row">
            {resultCode !== 0 && (
              <div className="icon-alert items-start mr-2 mt-1 text-upcolor"></div>
            )}
            <div className="font-bold text-bluegray-700 text-base">
              {getSyncAccountAssetResultCodeTitle(resultCode)}
            </div>
            <div
              className="icon-close ml-auto text-gray-500 cursor-pointer"
              onClick={close}
            />
          </div>
          <div className="mt-3 text-sm">
            {getSyncAccountAssetResultCodeMessage(resultCode)}
          </div>
          {syncAccountAssetResponse.resultCode !== 1 ? (
            <div
              onClick={close}
              className="flex justify-center text-blue-700 mt-4 p-2 text-sm cursor-pointer">
              閉じる
            </div>
          ) : (
            <></>
          )}
        </div>
      </Modal2>
    );
  }

  return (
    <Modal2 isShow={isShow}>
      <div className="p-4 max-h-modal overflow-y-auto">
        <div className="flex flex-row">
          <div className="icon-alert items-start mr-2 mt-1 text-upcolor"></div>
          <div className="font-bold text-bluegray-700 text-base">
            更新を停止しています
          </div>
          <div
            className="icon-close ml-auto text-gray-500 cursor-pointer"
            onClick={hide}
          />
        </div>
        <div className="mt-4 text-sm text-bluegray-700">
          セキュリティ強化の取り組みとして、３ヶ月間以上ログインがない状態が続くと口座情報の取得を一時停止いたします。
        </div>
        <div className="mt-6 text-sm text-bluegray-700">
          口座情報取得を再開するには、下記の更新ボタンより更新を行なってください。
        </div>
        <button
          className="mt-4 py-3 text-white bg-bluegray-700 justify-center w-full text-base rounded-lg"
          onClick={refreshAllAccountInfo}>
          全口座情報を更新
        </button>
      </div>
    </Modal2>
  );
};

export const getSyncAccountAssetResultCodeTitle = (resultCode: number) => {
  if (resultCode === 0) {
    return (
      <>
        データ更新を開始します
        <br />
        完了までしばらくお待ちください
      </>
    );
  } else if (resultCode === 1) {
    return <>定期更新が完了するまでお待ちください</>;
  } else if (resultCode === 2) {
    return <>口座についてお問合せください</>;
  } else if (resultCode === 3) {
    return <>既に更新処理を受け付けております</>;
  }
  return <>更新できませんでした</>;
};

export const getSyncAccountAssetResultCodeMessage = (resultCode: number) => {
  if (resultCode === 0) {
    return (
      <>
        <div>
          完了には10分〜数時間かかる場合があります。
          <br />
          連携先の証券会社がメンテナンス等で接続できない場合は、データ更新は翌営業日の夕方18：00以降となります。
        </div>
        <div className="mt-4">
          翌営業日以降もデータが更新されない場合はお問い合わせください。
        </div>
      </>
    );
  } else if (resultCode === 1) {
    return (
      <>
        <div>
          アセプラが定期更新中のため以下の時間帯は更新ができません。定期更新完了後に自動でデータが更新されます。
        </div>
        <div className="mt-4">
          【定期更新中の時間】
          <br />
          ①6:00〜10:00
          <br />
          ②16:00〜20:00
        </div>
        <div className="mt-4 text-xs">
          <div>※日曜日は除きます。</div>
          <div>※土曜日は①の時間帯だけ定期更新が発生します。</div>
        </div>
      </>
    );
  } else if (resultCode === 2) {
    return (
      <>
        <div>しばらく経っても解消しない場合はお問い合わせください。</div>
        <div className="mt-4 text-blue-700">
          <a
            href="https://support.minkabu.jp/hc/ja/requests/new?ticket_form_id=360002511233"
            target="_blank"
            rel="noopener noreferrer">
            <div className="flex flex-row">
              <span>お問い合わせ</span>
              <i className="icon-external-link ml-2 text-lg" />
            </div>
          </a>
        </div>
      </>
    );
  } else if (resultCode === 3) {
    return (
      <>
        <div>
          再度更新を行う場合は、1時間程度経ってから更新ボタンを押してください。
        </div>
        <div className="mt-4">
          翌営業日もデータが更新されない場合はお問い合わせください。
        </div>
        <div className="mt-4 text-blue-700">
          <a
            href="https://support.minkabu.jp/hc/ja/requests/new?ticket_form_id=360002511233"
            target="_blank"
            rel="noopener noreferrer">
            お問い合わせ
          </a>
        </div>
      </>
    );
  } else {
    return (
      <>
        <div>
          しばらくお待ちいただいても解消しない場合は、お手数をおかけしますがこちらからお問い合わせをお願いいたします。
        </div>
        <div className="mt-4 text-blue-700">
          <a
            href="https://support.minkabu.jp/hc/ja/requests/new?ticket_form_id=360002511233"
            target="_blank"
            rel="noopener noreferrer">
            お問い合わせ
          </a>
        </div>
      </>
    );
  }
};
